import { config, type GetToken } from '~/config'

// Notification Hub client (ported minimal from Nova/dashboard). Zunou's chat
// notifications are SENDER-CLIENT triggered — the API does not broadcast typing
// or reactions, so if we don't POST them here, nobody sees "…is typing" and a
// reaction notifies no one. (Team messages ARE additionally server-triggered by
// the API, so notifyTeamMessage here is idempotent belt-and-suspenders — it
// guarantees delivery even where the server trigger is off, e.g. staging.)
//
// The hub fans out over the same Pusher app we already subscribe to on
// private-users.{userId} (.typing / .team-reacted / .team-message).

const TYPING_DEBOUNCE_MS = 2000
const TYPING_AUTO_STOP_MS = 5000

interface NotifyTeamMessage {
  pulseId: string
  pulseName: string
  senderId: string
  senderName: string
  senderGravatar?: string | null
  messageId: string
  messagePreview: string
  organizationId: string
  teamThreadId?: string | null
  /** Topic the message was posted into (null = pulse main channel). Carried so
   *  the hub can honor a per-topic mute — without it every message reads as
   *  main-channel and a muted topic still notifies. */
  topicId?: string | null
  topicName?: string | null
  mentionedUserIds?: string[]
}

interface NotifyReaction {
  pulseId: string
  messageId: string
  emoji: string
  userId: string
  userName: string
  userGravatar?: string | null
  organizationId: string
  action?: 'add' | 'remove'
}

interface NotifyTyping {
  pulseId: string
  userId: string
  userName: string
  userGravatar?: string | null
  organizationId: string
  isTyping: boolean
}

class HubClient {
  private base: string
  private getToken: GetToken
  private typing = new Map<string, { timeout: ReturnType<typeof setTimeout>; lastSent: number }>()

  constructor(getToken: GetToken) {
    this.base = config.notificationHubUrl.replace(/\/$/, '')
    this.getToken = getToken
  }

  private async post(path: string, body: Record<string, unknown>): Promise<void> {
    try {
      const token = await this.getToken()
      if (!token) return
      await fetch(`${this.base}${path}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
    } catch {
      // Best effort — a notify failure must never break the send it follows.
    }
  }

  notifyTeamMessage(d: NotifyTeamMessage): void {
    void this.post('/notify/team-message', {
      created_at: new Date().toISOString(),
      mentioned_user_ids: d.mentionedUserIds ?? [],
      message_id: d.messageId,
      message_preview: (d.messagePreview || '').substring(0, 100),
      organization_id: d.organizationId,
      pulse_id: d.pulseId,
      pulse_name: d.pulseName,
      sender_gravatar: d.senderGravatar || '',
      sender_id: d.senderId,
      sender_name: d.senderName,
      thread_id: d.teamThreadId || null,
      topic_id: d.topicId ?? null,
      topic_name: d.topicName ?? null,
    })
  }

  notifyReaction(d: NotifyReaction): void {
    void this.post('/notify/reaction', {
      action: d.action || 'add',
      emoji: d.emoji,
      message_id: d.messageId,
      organization_id: d.organizationId,
      pulse_id: d.pulseId,
      type: 'team',
      user_gravatar: d.userGravatar || '',
      user_id: d.userId,
      user_name: d.userName,
    })
  }

  /** Debounced (per pulse), with an auto-stop so a "started typing" that never
   *  gets an explicit stop clears itself — matches Nova's contract exactly. */
  notifyTyping(d: NotifyTyping): void {
    const key = d.pulseId
    if (!d.isTyping) {
      const state = this.typing.get(key)
      if (state?.timeout) clearTimeout(state.timeout)
      this.typing.delete(key)
      void this.post('/notify/typing', {
        is_typing: false,
        organization_id: d.organizationId,
        pulse_id: key,
        user_gravatar: d.userGravatar || '',
        user_id: d.userId,
        user_name: d.userName,
      })
      return
    }
    const state = this.typing.get(key)
    const now = Date.now()
    if (state && now - state.lastSent < TYPING_DEBOUNCE_MS) return
    if (state?.timeout) clearTimeout(state.timeout)
    this.typing.set(key, {
      lastSent: now,
      timeout: setTimeout(() => this.notifyTyping({ ...d, isTyping: false }), TYPING_AUTO_STOP_MS),
    })
    void this.post('/notify/typing', {
      is_typing: true,
      organization_id: d.organizationId,
      pulse_id: key,
      user_gravatar: d.userGravatar || '',
      user_id: d.userId,
      user_name: d.userName,
    })
  }

  clearTyping(pulseId: string): void {
    const state = this.typing.get(pulseId)
    if (state?.timeout) clearTimeout(state.timeout)
    this.typing.delete(pulseId)
  }
}

let instance: HubClient | null = null
/** Singleton hub client (token getter kept fresh across Auth0 rotation). */
export function getHub(getToken: GetToken): HubClient {
  if (!instance) instance = new HubClient(getToken)
  return instance
}
export type { HubClient }
